// TelcoTrack — weld / connector capacity model (FTTH v25 + PI-0045 / PI-0056).
// A single `maxWeldCount` is not enough: factory included ≠ expandable max,
// ribbon ≠ single-fiber, and RAY-TR3 / MUK / PON32 depend on osazení.
//
// `maxWeldCount` on an identity is derived only when capacityMode === 'fixed'
// and the weld unit is a real weld (single-fiber or ribbon). Everything else
// stays null and is computed live from cassettes / OFSM modules / materials.

import type { RozvadecCabinetTypeId, RozvadecMaterialUsageLike } from './rozvadec-cabinet.constants';
import { getRozvadecCassetteType, isRozvadecKm5FamilyCassette } from './rozvadec-cassette.constants';
import { getRayOfsmModule } from './rozvadec-ofsm.constants';

export const ROZVADEC_WELD_UNITS = ['single-fiber', 'ribbon'] as const;
export type RozvadecWeldUnit = (typeof ROZVADEC_WELD_UNITS)[number];

export const ROZVADEC_CAPACITY_MODES = [
  'fixed',
  'tray-based',
  'module-based',
  'configuration-based',
] as const;
export type RozvadecCapacityMode = (typeof ROZVADEC_CAPACITY_MODES)[number];

/**
 * Per-SAP (or per-type) capacity profile.
 * Connector / tray-seat numbers are never aliased as weld caps.
 */
export interface RozvadecWeldCapacityProfile {
  includedWeldCapacity: number | null;
  expandableWeldCapacity: number | null;
  weldUnit: RozvadecWeldUnit;
  capacityMode: RozvadecCapacityMode;
  /** Connector / adapter field. Null when the item has no documented connector ceiling. */
  includedConnectorCapacity: number | null;
  expandableConnectorCapacity: number | null;
  /**
   * SFMS theoretical tray seats (1× SC cassette 6 + 9× SE cassette 12 = 114).
   * Tray positions, not required welds. Splitter ratio is not weld capacity.
   */
  expandableTraySeatCount: number | null;
}

export const EMPTY_WELD_CAPACITY_PROFILE: RozvadecWeldCapacityProfile = {
  includedWeldCapacity: null,
  expandableWeldCapacity: null,
  weldUnit: 'single-fiber',
  capacityMode: 'configuration-based',
  includedConnectorCapacity: null,
  expandableConnectorCapacity: null,
  expandableTraySeatCount: null,
};

export type RozvadecWeldCapacityResolveInput = {
  typeId?: string | null;
  derivedFromSap?: string | null;
  materials?: readonly RozvadecMaterialUsageLike[] | null;
  /** Installed weld-layout cassettes. When present they win over cassette SAP counts. */
  cassettes?: readonly { cassetteType: string }[] | null;
  /** Installed RAY-OFSM modules. When present they win over OFSM SAP counts. */
  ofsmModules?: readonly { materialSap: string }[] | null;
};

export type RozvadecResolvedWeldCapacity = {
  profile: RozvadecWeldCapacityProfile;
  /** Live weld ceiling. Null = do not invent a number. */
  weldLimit: number | null;
  connectorLimit: number | null;
  /** True when this is OS and no approved / computable weld ceiling exists. */
  unknownOsLimit: boolean;
  /** Backward compat: only `capacityMode === 'fixed'` with an unambiguous weld unit. */
  maxWeldCount: number | null;
};

function profile(partial: Partial<RozvadecWeldCapacityProfile>): RozvadecWeldCapacityProfile {
  return { ...EMPTY_WELD_CAPACITY_PROFILE, ...partial };
}

/** Factory included that is also the documented ceiling (STP-XL / BPEO ribbon). */
function fixedWelds(included: number, unit: RozvadecWeldUnit = 'single-fiber'): RozvadecWeldCapacityProfile {
  return profile({
    includedWeldCapacity: included,
    expandableWeldCapacity: included,
    weldUnit: unit,
    capacityMode: 'fixed',
  });
}

/**
 * TENIO — Optická přístupová síť CETIN (FTTH) v25.
 * Catalog “max” (144 / 288) is expandable, not the factory included sestava.
 * 325760 name says 144; body is C6H with 4×S24 = 96 installed, expand to 288.
 */
const TENIO_325735 = profile({
  includedWeldCapacity: 192,
  expandableWeldCapacity: 288,
  capacityMode: 'tray-based',
});
const TENIO_325736 = profile({
  includedWeldCapacity: 96,
  expandableWeldCapacity: 144,
  capacityMode: 'tray-based',
});
const TENIO_325760 = profile({
  includedWeldCapacity: 96,
  expandableWeldCapacity: 288,
  capacityMode: 'tray-based',
});
const TENIO_325738 = profile({
  includedWeldCapacity: 192,
  expandableWeldCapacity: 288,
  capacityMode: 'tray-based',
});

/**
 * COYOTE SFMS — splitter 1:16/1:32/1:64 is not weld capacity.
 * Hookup welds would be 17 / 33 / 65; leaving included null so we do not
 * collapse 16/32/64 into one number named like weld cap.
 * 114 is tray seats only (1×6 + 9×12), not a product-approved weld ceiling.
 */
function sfmsProfile(): RozvadecWeldCapacityProfile {
  return profile({
    capacityMode: 'configuration-based',
    expandableTraySeatCount: 114,
  });
}

/**
 * Identity SAP → methodology profile.
 * 20 OS SAPs: 5 fixed/ribbon or STP included, TENIO included+expandable,
 * BPEO tray/connector, COYOTE/SFMS null. Do not invent COYOTE 180 (that is SAP 318525).
 */
export const ROZVADEC_WELD_CAPACITY_BY_SAP: Readonly<Record<string, RozvadecWeldCapacityProfile>> = {
  '324363': profile({ capacityMode: 'configuration-based' }),
  '324362': profile({ capacityMode: 'configuration-based' }),
  '326274': profile({
    capacityMode: 'tray-based',
    expandableWeldCapacity: 576,
    expandableConnectorCapacity: 144,
  }),
  '326273': profile({
    capacityMode: 'tray-based',
    expandableWeldCapacity: 1152,
    expandableConnectorCapacity: 288,
  }),
  '325715': profile({ capacityMode: 'module-based' }),
  '325701': profile({ capacityMode: 'module-based' }),
  '325739': fixedWelds(32, 'ribbon'),
  '327337': profile({ capacityMode: 'tray-based' }),
  '325737': profile({ capacityMode: 'configuration-based' }),
  '319647': profile({ capacityMode: 'configuration-based' }),
  '320184': profile({ capacityMode: 'configuration-based', weldUnit: 'ribbon' }),
  '320183': profile({ capacityMode: 'configuration-based', weldUnit: 'ribbon' }),
  '318524': profile({ capacityMode: 'configuration-based' }),
  '306732': profile({ capacityMode: 'configuration-based' }),
  '325532': sfmsProfile(),
  '324995': sfmsProfile(),
  '324994': sfmsProfile(),
  '325764': fixedWelds(120),
  '325765': fixedWelds(192),
  '325766': profile({ capacityMode: 'configuration-based' }),
  '325767': fixedWelds(192),
  '325768': fixedWelds(32, 'ribbon'),
  '325736': TENIO_325736,
  '325760': TENIO_325760,
  '325735': TENIO_325735,
  '325738': TENIO_325738,
};

/**
 * Non-identity materials that contribute capacity (do not set cabinet typeId).
 * RAY-OFSM = connector field of the module, not RAY-TR3 = 144.
 * 327338 = BPEO cassette 24 single-fiber welds.
 * 324364 = standalone ODF 144 connector field (not an alias of RAY-TR3).
 * 323177 = standalone ORSM 3P — no documented weld/connector ceiling in repo.
 */
export const ROZVADEC_ACCESSORY_CAPACITY_BY_SAP: Readonly<Record<string, RozvadecWeldCapacityProfile>> = {
  '327338': profile({
    includedWeldCapacity: 24,
    expandableWeldCapacity: 24,
    capacityMode: 'tray-based',
  }),
  '325704': profile({
    includedConnectorCapacity: 144,
    expandableConnectorCapacity: 144,
    includedWeldCapacity: 144,
    expandableWeldCapacity: 144,
    capacityMode: 'module-based',
  }),
  '325713': profile({
    includedConnectorCapacity: 96,
    expandableConnectorCapacity: 96,
    includedWeldCapacity: 96,
    expandableWeldCapacity: 96,
    capacityMode: 'module-based',
  }),
  '325712': profile({
    includedConnectorCapacity: 48,
    expandableConnectorCapacity: 48,
    includedWeldCapacity: 48,
    expandableWeldCapacity: 48,
    capacityMode: 'module-based',
  }),
  '326203': profile({
    includedConnectorCapacity: 48,
    expandableConnectorCapacity: 48,
    includedWeldCapacity: 48,
    expandableWeldCapacity: 48,
    capacityMode: 'module-based',
  }),
  '325702': profile({
    capacityMode: 'module-based',
  }),
  '325703': profile({
    capacityMode: 'module-based',
  }),
  '324364': profile({
    includedConnectorCapacity: 144,
    expandableConnectorCapacity: 144,
    capacityMode: 'fixed',
  }),
  '323177': profile({ capacityMode: 'configuration-based' }),
};

/**
 * Type-level profiles when there is no identity SAP (SDF) or the identity
 * has no inherent cap (RAY-TR3). 384 / 432 are fiber / connector field sizes,
 * not weld counts. RSU weld/connector stay null until OFSM is counted.
 */
export const ROZVADEC_TYPE_WELD_CAPACITY: Readonly<
  Partial<Record<RozvadecCabinetTypeId, RozvadecWeldCapacityProfile>>
> = {
  SDF_384: profile({
    capacityMode: 'module-based',
    expandableConnectorCapacity: 384,
  }),
  SDF_432: profile({
    capacityMode: 'module-based',
    expandableConnectorCapacity: 432,
  }),
  RSU_ODF: profile({ capacityMode: 'module-based' }),
  OS: profile({ capacityMode: 'configuration-based' }),
};

const BPEO_CASSETTE_SAP = '327338';

const OS_IDENTITY_SAPS = new Set([
  '325739',
  '327337',
  '325737',
  '319647',
  '320184',
  '320183',
  '318524',
  '306732',
  '325532',
  '324995',
  '324994',
  '325764',
  '325765',
  '325766',
  '325767',
  '325768',
  '325736',
  '325760',
  '325735',
  '325738',
]);

const MUK_IDENTITY_SAPS = new Set(['326274', '326273']);
const RSU_IDENTITY_SAPS = new Set(['325715', '325701']);
const PON32_IDENTITY_SAPS = new Set(['324363', '324362']);

export function getRozvadecWeldCapacityProfile(
  sapCode: string | null | undefined,
): RozvadecWeldCapacityProfile | undefined {
  const sap = String(sapCode ?? '').trim();
  if (!sap) return undefined;
  return ROZVADEC_WELD_CAPACITY_BY_SAP[sap] ?? ROZVADEC_ACCESSORY_CAPACITY_BY_SAP[sap];
}

export function getRozvadecTypeWeldCapacityProfile(
  typeId: string | null | undefined,
): RozvadecWeldCapacityProfile | undefined {
  const key = String(typeId ?? '').trim() as RozvadecCabinetTypeId;
  return ROZVADEC_TYPE_WELD_CAPACITY[key];
}

/**
 * Legacy `maxWeldCount`: only a fixed, unambiguous weld ceiling.
 * TENIO / MUK / PON / RAY-TR3 stay null.
 */
export function derivedIdentityMaxWeldCount(
  capacity: RozvadecWeldCapacityProfile | null | undefined,
): number | null {
  if (!capacity || capacity.capacityMode !== 'fixed') return null;
  const n = capacity.includedWeldCapacity;
  if (n == null || !Number.isFinite(n) || n <= 0) return null;
  return n;
}

/**
 * OK / OS provařovací plochy: always one free seat after used ones, up to max.
 * used=0 → 1; used=5 → 6; used=max → max (no extra empty).
 */
export function growingWeldSeatCount(
  used: number,
  maxWeldCount?: number | null,
): number {
  const filled = Number.isFinite(used) ? Math.max(0, Math.floor(used)) : 0;
  const next = filled + 1;
  if (maxWeldCount != null && Number.isFinite(maxWeldCount) && maxWeldCount > 0) {
    return Math.min(next, Math.floor(maxWeldCount));
  }
  return next;
}

export function attachRozvadecIdentityCapacity<
  T extends { sapCode: string; maxWeldCount?: number | null },
>(row: T): T & { capacity: RozvadecWeldCapacityProfile; maxWeldCount: number | null } {
  const capacity = getRozvadecWeldCapacityProfile(row.sapCode) ?? EMPTY_WELD_CAPACITY_PROFILE;
  return {
    ...row,
    capacity,
    maxWeldCount: derivedIdentityMaxWeldCount(capacity),
  };
}

function rowSap(row: RozvadecMaterialUsageLike): string {
  return String(row.sapCode ?? row.materialCode ?? '').trim();
}

function rowQty(row: RozvadecMaterialUsageLike): number {
  if (row.quantity == null) return 1;
  if (!Number.isFinite(row.quantity) || row.quantity <= 0) return 0;
  return Math.floor(row.quantity);
}

export function sumMaterialQuantityBySap(
  used: readonly RozvadecMaterialUsageLike[] | null | undefined,
  sapCode: string,
): number {
  const sap = sapCode.trim();
  if (!sap || !Array.isArray(used)) return 0;
  let total = 0;
  for (const row of used) {
    if (!row || rowSap(row) !== sap) continue;
    total += rowQty(row);
  }
  return total;
}

function minPositive(values: readonly (number | null | undefined)[]): number | null {
  const known = values.filter((n): n is number => n != null && Number.isFinite(n) && n > 0);
  if (known.length === 0) return null;
  return Math.min(...known);
}

function mergeProfiles(profiles: readonly RozvadecWeldCapacityProfile[]): RozvadecWeldCapacityProfile {
  if (profiles.length === 0) return EMPTY_WELD_CAPACITY_PROFILE;
  if (profiles.length === 1) return profiles[0];
  const units = new Set(profiles.map((p) => p.weldUnit));
  const modes = new Set(profiles.map((p) => p.capacityMode));
  return profile({
    includedWeldCapacity: minPositive(profiles.map((p) => p.includedWeldCapacity)),
    expandableWeldCapacity: minPositive(profiles.map((p) => p.expandableWeldCapacity)),
    weldUnit: units.size === 1 ? profiles[0].weldUnit : 'single-fiber',
    capacityMode: modes.size === 1 ? profiles[0].capacityMode : 'configuration-based',
    includedConnectorCapacity: minPositive(profiles.map((p) => p.includedConnectorCapacity)),
    expandableConnectorCapacity: minPositive(profiles.map((p) => p.expandableConnectorCapacity)),
    expandableTraySeatCount: minPositive(profiles.map((p) => p.expandableTraySeatCount)),
  });
}

function collectIdentitySaps(input: RozvadecWeldCapacityResolveInput): string[] {
  const saps: string[] = [];
  const seen = new Set<string>();
  const push = (sap: string | null | undefined) => {
    const key = String(sap ?? '').trim();
    if (!key || seen.has(key) || !ROZVADEC_WELD_CAPACITY_BY_SAP[key]) return;
    seen.add(key);
    saps.push(key);
  };
  if (Array.isArray(input.materials)) {
    for (const row of input.materials) push(rowSap(row));
  }
  push(input.derivedFromSap);
  return saps;
}

function countInstalledCassettes(input: RozvadecWeldCapacityResolveInput): {
  km4: number;
  km5: number;
  km6: number;
} {
  if (Array.isArray(input.cassettes) && input.cassettes.length > 0) {
    let km4 = 0;
    let km5 = 0;
    let km6 = 0;
    for (const cassette of input.cassettes) {
      const id = getRozvadecCassetteType(cassette.cassetteType)?.id;
      if (id === 'KM4') km4 += 1;
      else if (id === 'KM6') km6 += 1;
      else if (id && isRozvadecKm5FamilyCassette(id)) km5 += 1;
    }
    return { km4, km5, km6 };
  }
  const materials = input.materials;
  return {
    km4: sumMaterialQuantityBySap(materials, '326275'),
    km5:
      sumMaterialQuantityBySap(materials, '326276') +
      sumMaterialQuantityBySap(materials, '326278') +
      sumMaterialQuantityBySap(materials, '326279'),
    km6: 0,
  };
}

function ofsmCapacityFromSap(sapCode: string): { connectors: number; welds: number } {
  const def = getRayOfsmModule(sapCode);
  if (def) return { connectors: def.connectorCapacity, welds: def.weldCapacity };
  return { connectors: 0, welds: 0 };
}

function ofsmLiveCapacity(input: RozvadecWeldCapacityResolveInput): { connectors: number; welds: number } {
  if (Array.isArray(input.ofsmModules) && input.ofsmModules.length > 0) {
    return input.ofsmModules.reduce(
      (sum, module) => {
        const part = ofsmCapacityFromSap(module.materialSap);
        return { connectors: sum.connectors + part.connectors, welds: sum.welds + part.welds };
      },
      { connectors: 0, welds: 0 },
    );
  }
  const materials = input.materials;
  const saps = ['325704', '325713', '325712', '326203', '325702', '325703'] as const;
  return saps.reduce(
    (sum, sap) => {
      const qty = sumMaterialQuantityBySap(materials, sap);
      if (qty <= 0) return sum;
      const part = ofsmCapacityFromSap(sap);
      return {
        connectors: sum.connectors + part.connectors * qty,
        welds: sum.welds + part.welds * qty,
      };
    },
    { connectors: 0, welds: 0 },
  );
}

function standaloneOdfConnectorCapacity(
  materials: readonly RozvadecMaterialUsageLike[] | null | undefined,
): number {
  return sumMaterialQuantityBySap(materials, '324364') * 144;
}

function bpeoCassetteWeldCapacity(materials: readonly RozvadecMaterialUsageLike[] | null | undefined): number {
  return sumMaterialQuantityBySap(materials, BPEO_CASSETTE_SAP) * 24;
}

function documentedWeldCeiling(profile: RozvadecWeldCapacityProfile): number | null {
  return profile.expandableWeldCapacity ?? profile.includedWeldCapacity;
}

/**
 * Live weld / connector limits from identity profiles + osazení.
 *
 * Tray-based MUK: weld = 24 × (KM4 + KM5), adapters = 12 × KM5.
 * PON32 324363: weld = 24 × installed KM6 (PI-0045). Empty rack → null, not 96.
 * 324362: no confirmed KM6 internals — do not copy 4×KM6.
 * RAY-TR3: connector + weld = Σ installed OFSM (splitters add no ordinary weld seats).
 * Empty rack → both null.
 * BPEO 327337: weld = count(327338) × 24 (0 cassettes → 0, not unknown).
 */
export function resolveRozvadecLiveWeldCapacity(
  input: RozvadecWeldCapacityResolveInput,
): RozvadecResolvedWeldCapacity {
  const identitySaps = collectIdentitySaps(input);
  const identityProfiles = identitySaps.map((sap) => ROZVADEC_WELD_CAPACITY_BY_SAP[sap]);
  const typeProfile = getRozvadecTypeWeldCapacityProfile(input.typeId);
  const profileMerged = mergeProfiles([
    ...identityProfiles,
    ...(typeProfile && identityProfiles.length === 0 ? [typeProfile] : []),
  ]);
  const typeId = String(input.typeId ?? '').trim();
  const os = typeId === 'OS' || identitySaps.some((sap) => OS_IDENTITY_SAPS.has(sap));
  const muk =
    typeId === 'MUK_1X24' ||
    typeId === 'MUK_2X24' ||
    identitySaps.some((sap) => MUK_IDENTITY_SAPS.has(sap));
  const rsu = typeId === 'RSU_ODF' || identitySaps.some((sap) => RSU_IDENTITY_SAPS.has(sap));
  const pon32 = typeId === 'PON32' || identitySaps.some((sap) => PON32_IDENTITY_SAPS.has(sap));

  const trays = countInstalledCassettes(input);
  let weldLimit: number | null = null;
  let connectorLimit: number | null = null;

  if (identitySaps.includes('327337')) {
    weldLimit = bpeoCassetteWeldCapacity(input.materials);
  }

  if (muk) {
    const installed = trays.km4 + trays.km5;
    if (installed > 0) {
      weldLimit = 24 * installed;
      if (trays.km5 > 0) connectorLimit = 12 * trays.km5;
    }
  }

  if (pon32 && trays.km6 > 0) {
    // PI-0045: weld cap follows installed KM6. Empty rack → do not invent 96.
    weldLimit = 24 * trays.km6;
  }

  if (rsu) {
    const ofsm = ofsmLiveCapacity(input);
    const connectors = ofsm.connectors + standaloneOdfConnectorCapacity(input.materials);
    connectorLimit = connectors > 0 ? connectors : null;
    weldLimit = ofsm.welds > 0 ? ofsm.welds : null;
  }

  if (typeId === 'SDF_384' || typeId === 'SDF_432') {
    connectorLimit = typeProfile?.expandableConnectorCapacity ?? null;
    weldLimit = null;
  }

  if (weldLimit == null && !muk && !rsu && !pon32) {
    const sfmsTrayOnly =
      profileMerged.capacityMode === 'configuration-based' && profileMerged.expandableTraySeatCount != null;
    if (!sfmsTrayOnly) {
      weldLimit = documentedWeldCeiling(profileMerged);
    }
  }

  if (connectorLimit == null && !rsu && !muk) {
    connectorLimit =
      profileMerged.expandableConnectorCapacity ?? profileMerged.includedConnectorCapacity ?? null;
  }

  const maxWeldCount =
    identityProfiles.length === 1
      ? derivedIdentityMaxWeldCount(identityProfiles[0])
      : derivedIdentityMaxWeldCount(profileMerged);
  const unknownOsLimit = os && weldLimit == null;

  return {
    profile: profileMerged,
    weldLimit,
    connectorLimit,
    unknownOsLimit,
    maxWeldCount,
  };
}

export function isRozvadecCapacityMode(value: string | null | undefined): value is RozvadecCapacityMode {
  return (ROZVADEC_CAPACITY_MODES as readonly string[]).includes(String(value ?? '').trim());
}

export function isRozvadecWeldUnit(value: string | null | undefined): value is RozvadecWeldUnit {
  return (ROZVADEC_WELD_UNITS as readonly string[]).includes(String(value ?? '').trim());
}
